import { formatDateTime, routes, type SessionInfo } from '@pmai/contracts';
import { Button, ConfirmDialog, DataTable, PageHeader, StatusBadge } from '@pmai/ui';
import { useTranslation } from 'react-i18next';
import { useApiQuery } from '../../shared/api/hooks';
import { useConfirm } from '../../shared/use-confirm';

/** "Browser on OS" from a user-agent string — enough to recognise one's own devices. */
function deviceName(userAgent: string | null, unknown: string): string {
  if (!userAgent) return unknown;
  const browser = /Edg\//.test(userAgent)
    ? 'Edge'
    : /Firefox\//.test(userAgent)
      ? 'Firefox'
      : /Chrome\//.test(userAgent)
        ? 'Chrome'
        : /Safari\//.test(userAgent)
          ? 'Safari'
          : null;
  const os = /Windows/.test(userAgent)
    ? 'Windows'
    : /Mac OS X/.test(userAgent)
      ? 'macOS'
      : /Android/.test(userAgent)
        ? 'Android'
        : /iPhone|iPad/.test(userAgent)
          ? 'iOS'
          : /Linux/.test(userAgent)
            ? 'Linux'
            : null;
  return [browser, os].filter(Boolean).join(' · ') || userAgent.slice(0, 40);
}

/** FR-WS-010: own active sessions; end the others. */
export function SessionsPage() {
  const { t } = useTranslation();
  const sessions = useApiQuery(routes.auth.sessions);
  const end = useConfirm(routes.auth.revokeSession, {
    invalidates: [routes.auth.sessions],
    params: (s: SessionInfo) => ({ id: s.id }),
  });
  const unknown = t('workspace.sessions.unknownDevice');

  return (
    <>
      <PageHeader title={t('workspace.sessions.title')} />
      <DataTable
        label={t('workspace.sessions.title')}
        rows={sessions.data}
        rowKey={(s) => s.id}
        loading={sessions.isPending}
        empty={null}
        columns={[
          {
            key: 'device',
            header: t('workspace.sessions.device'),
            cell: (s) => (
              <span className="flex items-center gap-2">
                {deviceName(s.userAgent, unknown)}
                {s.current && <StatusBadge tone="success" label={t('workspace.sessions.current')} />}
              </span>
            ),
          },
          { key: 'ip', header: t('workspace.sessions.ip'), cell: (s) => s.ip ?? '–' },
          {
            key: 'created',
            header: t('workspace.sessions.created'),
            cell: (s) => formatDateTime(s.createdAt),
          },
          {
            key: 'lastSeen',
            header: t('workspace.sessions.lastSeen'),
            cell: (s) => formatDateTime(s.lastSeenAt),
          },
          {
            key: 'actions',
            header: '',
            align: 'right',
            cell: (s) =>
              !s.current && (
                <Button variant="ghost" size="sm" onClick={() => end.ask(s)}>
                  {t('workspace.sessions.end')}
                </Button>
              ),
          },
        ]}
      />
      <ConfirmDialog
        {...end.dialog}
        title={t('workspace.sessions.endTitle')}
        message={t('workspace.sessions.endText', {
          device: end.target ? deviceName(end.target.userAgent, unknown) : '',
        })}
        confirmLabel={t('workspace.sessions.end')}
      />
    </>
  );
}
